import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  ShieldAlert,
  Upload,
  Camera,
  CheckCircle2,
  AlertCircle,
  FileText,
  Clock,
  RefreshCw,
  Eye,
  ZoomIn,
  ZoomOut,
  RotateCw,
  X,
  ArrowLeft,
  Lock,
  ChevronRight,
  Info,
  Sparkles,
  UserCheck,
  Zap,
} from 'lucide-react';
import { KYCDocumentType, KYCStatus, KYCSubmission } from '../types';
import { SupportedDocumentType } from '../types/verification.types';
import { apiFetch as fetch } from '../lib/apiConfig';
import { auth, storage } from '../lib/firebase';
import { ref, uploadBytes } from 'firebase/storage';
import { uploadDocumentSide, submitDocumentsForReview } from '../lib/verificationService';
import { capturePhoto } from '../lib/nativeBridge';
import { isNativeApp } from '../lib/apiConfig';
import {
  validateProfileSubmission,
  canSubmitProfile,
} from '../utils/documentValidation';
import { DateOfBirthInput } from '../components/common/DateOfBirthInput';
import { validateAdultDob } from '../utils/dobUtils';

interface DocumentConfig {
  type: KYCDocumentType;
  title: string;
  subtitle: string;
  badge: string;
  sidesRequired: 'BOTH' | 'FRONT_ONLY';
  frontLabel: string;
  frontHelp: string;
  backLabel?: string;
  backHelp?: string;
  idFormatHint: string;
}

const DOCUMENT_CONFIGS: Record<KYCDocumentType, DocumentConfig> = {
  AADHAAR: {
    type: 'AADHAAR',
    title: 'Aadhaar Card',
    subtitle: '12-digit Unique Identification Authority of India (UIDAI)',
    badge: 'Front + Back Required (à¦¦à§à¦‡ à¦ªà¦¾à¦¶)',
    sidesRequired: 'BOTH',
    frontLabel: 'FRONT SIDE (à¦¸à¦¾à¦®à¦¨à§‡à¦° à¦ªà¦¾à¦¶)',
    frontHelp: 'Must show clear photo, full legal name, DOB, and 12-digit number.',
    backLabel: 'BACK SIDE (à¦ªà§‡à¦›à¦¨à§‡à¦° à¦ªà¦¾à¦¶)',
    backHelp: 'Must show QR code, permanent residential address, and pin code.',
    idFormatHint: 'e.g. 5412 8901 2345',
  },
  PAN: {
    type: 'PAN',
    title: 'PAN Card',
    subtitle: 'Permanent Account Number â€” Income Tax Department of India',
    badge: 'Front Only (à¦¶à§à¦§à§ Front à¦ªà¦¾à¦¶)',
    sidesRequired: 'FRONT_ONLY',
    frontLabel: 'FRONT SIDE (à¦¸à¦¾à¦®à¦¨à§‡à¦° à¦ªà¦¾à¦¶)',
    frontHelp: 'Must show clear photograph, PAN alphanumeric number, signature, and fatherâ€™s name.',
    idFormatHint: 'e.g. ABCDE1234F',
  },
  DRIVING_LICENSE: {
    type: 'DRIVING_LICENSE',
    title: 'Driving Licence',
    subtitle: 'State Transport Department Government of India',
    badge: 'Front + Back Required (à¦¦à§à¦‡ à¦ªà¦¾à¦¶)',
    sidesRequired: 'BOTH',
    frontLabel: 'FRONT SIDE (à¦¸à¦¾à¦®à¦¨à§‡à¦° à¦ªà¦¾à¦¶)',
    frontHelp: 'Must clearly show driver license number, issue date, class of vehicle, and your photo.',
    backLabel: 'BACK SIDE (à¦ªà§‡à¦›à¦¨à§‡à¦° à¦ªà¦¾à¦¶)',
    backHelp: 'Must show issuing authority stamp, emergency endorsement, and address.',
    idFormatHint: 'e.g. WB-01-20220019284',
  },
  PASSPORT: {
    type: 'PASSPORT',
    title: 'Passport',
    subtitle: 'Republic of India â€” Ministry of External Affairs',
    badge: 'Image 1 + Image 2 (Front + Back)',
    sidesRequired: 'BOTH',
    frontLabel: 'PAGE 1: DATA & PHOTO PAGE (à¦›à¦¬à¦¿ à¦“ à¦¤à¦¥à§à¦¯ à¦ªà¦¾à¦¤à¦¾)',
    frontHelp: 'Must clearly display passport number, photo, nationality, validity, and MRZ lines.',
    backLabel: 'PAGE 2: ADDRESS & PARENTS PAGE (à¦ à¦¿à¦•à¦¾à¦¨à¦¾ à¦ªà¦¾à¦¤à¦¾)',
    backHelp: 'Must clearly show permanent address, parent/spouse names, and file number.',
    idFormatHint: 'e.g. Z1234567',
  },
  VOTER_ID: {
    type: 'VOTER_ID',
    title: 'Voter ID (EPIC)',
    subtitle: 'Election Commission of India Identity Card',
    badge: 'Front + Back Required (দুই পাশ)',
    sidesRequired: 'BOTH',
    frontLabel: 'FRONT SIDE (সামনের পাশ)',
    frontHelp: 'Must clearly show photograph, full legal name, and EPIC alphanumeric number.',
    backLabel: 'BACK SIDE (পেছনের পাশ)',
    backHelp: 'Must clearly show permanent residential address and assembly constituency details.',
    idFormatHint: 'e.g. ABC1234567',
  },
};

export const KYCVerificationPage: React.FC = () => {
  const navigate = useNavigate();

  // Current user info from localStorage
  const [currentUserProfile, setCurrentUserProfile] = useState<any>(() => {
    try {
      const saved = localStorage.getItem('pairup_user_profile_data');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const userId = auth.currentUser?.uid || '';
  const userName = auth.currentUser?.displayName || currentUserProfile?.displayName || currentUserProfile?.name || 'PairUp Member';
  const userEmail = auth.currentUser?.email || currentUserProfile?.email || '';
  const userPhone = auth.currentUser?.phoneNumber || currentUserProfile?.phone || '';
  const userRole = (currentUserProfile?.accountType as 'client' | 'companion') || 'companion';

  // State machine status
  const [kycStatus, setKycStatus] = useState<KYCStatus>('NOT_STARTED');
  const [currentSubmission, setCurrentSubmission] = useState<KYCSubmission | null>(null);
  const [isLoadingStatus, setIsLoadingStatus] = useState<boolean>(true);

  // Form State
  const [selectedDocType, setSelectedDocType] = useState<KYCDocumentType>('AADHAAR');
  const [legalName, setLegalName] = useState<string>(currentUserProfile?.legalName || userName);
  const [idNumber, setIdNumber] = useState<string>('');
  const [gender, setGender] = useState<'Male' | 'Female'>(
    currentUserProfile?.gender === 'Female' || currentUserProfile?.companionProfile?.gender === 'female'
      ? 'Female'
      : 'Male'
  );
  const [dob, setDob] = useState<string>(currentUserProfile?.dob || '1998-05-14');
  const [dobError, setDobError] = useState<string | null>(null);

  // Uploaded Image Blobs/Data URLs
  const [frontImage, setFrontImage] = useState<string | null>(null);
  const [backImage, setBackImage] = useState<string | null>(null);
  const [selfieImage, setSelfieImage] = useState<string | null>(null);

  // Validation feedback
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitSuccessMessage, setSubmitSuccessMessage] = useState<string | null>(null);

  // Secure Image Viewer Modal State
  const [previewModalUrl, setPreviewModalUrl] = useState<string | null>(null);
  const [previewModalTitle, setPreviewModalTitle] = useState<string>('');
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  const [rotationDegrees, setRotationDegrees] = useState<number>(0);

  // Live Camera Capture Modal State
  const [cameraModalOpen, setCameraModalOpen] = useState<boolean>(false);
  const [cameraTarget, setCameraTarget] = useState<'FRONT' | 'BACK' | 'SELFIE'>('FRONT');
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // 1. Fetch user's existing KYC submission on mount
  useEffect(() => {
    async function fetchUserKyc() {
      setIsLoadingStatus(true);
      try {
        const firebaseUser = auth.currentUser;
        if (!firebaseUser) throw new Error("Please sign in before accessing KYC.");
        const idToken = await firebaseUser.getIdToken();
        const res = await fetch('/api/kyc/documents', {
          headers: { Authorization: `Bearer ${idToken}` },
        });

        if (!res.ok) {
          throw new Error(`Unable to load KYC documents (HTTP ${res.status}).`);
        }

        const data = await res.json();
        if (data.success !== true || !Array.isArray(data.documents)) {
          throw new Error('The KYC API returned an invalid response.');
        }

        const documents = data.documents;
        setCurrentSubmission(null);

        const statuses = documents.map((doc: { status?: string }) =>
          String(doc.status || '').toUpperCase()
        );

        if (statuses.includes('REJECTED')) {
          setKycStatus('REJECTED');
        } else if (statuses.length > 0 && statuses.every((status: string) => status === 'VERIFIED')) {
          setKycStatus('APPROVED');
        } else if (statuses.includes('UNDER_REVIEW')) {
          setKycStatus('PENDING_REVIEW');
        } else if (statuses.length > 0) {
          setKycStatus('IN_PROGRESS');
        } else {
          setKycStatus('NOT_STARTED');
        }

        const firstDoc = documents[0];
        if (firstDoc?.documentType) {
          const allowedTypes = ['AADHAAR', 'VOTER_ID', 'PAN', 'DRIVING_LICENSE', 'PASSPORT'];
          if (allowedTypes.includes(firstDoc.documentType)) {
            setSelectedDocType(firstDoc.documentType);
          }
        }
      } catch (e) {
        console.warn('Error fetching KYC status:', e);
      } finally {
        setIsLoadingStatus(false);
      }
    }
    fetchUserKyc();
  }, [userId, userName]);

  // Clean up camera stream on unmount
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream]);

  // Handle Document Selection
  const handleSelectDocType = (type: KYCDocumentType) => {
    setSelectedDocType(type);
    setValidationError(null);
    // If switched to PAN, clear back image since PAN does not have back
    if (type === 'PAN') {
      setBackImage(null);
    }
  };

  // Image validation helper
  const validateAndProcessFile = (
    file: File,
    onSuccess: (dataUrl: string) => void
  ) => {
    setValidationError(null);

    // Format validation (Section 4: Allow JPG, JPEG, PNG, PDF)
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type.toLowerCase())) {
      setValidationError('Unsupported format. Only JPG, JPEG, PNG, or PDF files are accepted.');
      return;
    }

    // Size limit: 10MB (Section 4)
    const maxSizeBytes = 10 * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      setValidationError('File is too large. Maximum allowed file size is 10MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (!dataUrl) {
        setValidationError('Failed to read file. Please try another document.');
        return;
      }

      // If PDF, directly pass through
      if (file.type.toLowerCase() === 'application/pdf') {
        onSuccess(dataUrl);
        return;
      }

      // Check image readability and resolution for images
      const img = new Image();
      img.onload = () => {
        if (img.width < 200 || img.height < 150) {
          setValidationError('Image resolution is too low. Please upload a clear, legible photo.');
          return;
        }
        onSuccess(dataUrl);
      };
      img.onerror = () => {
        setValidationError('Corrupted or invalid image file. Please select another photo.');
      };
      img.src = dataUrl;
    };
    reader.onerror = () => {
      setValidationError('Error reading file. Please try again.');
    };
    reader.readAsDataURL(file);
  };

  // Camera capture controls
  const startCameraCapture = async (target: 'FRONT' | 'BACK' | 'SELFIE') => {
    // If native mobile app, use Capacitor native camera
    if (isNativeApp()) {
      const nativePhoto = await capturePhoto({
        promptLabelHeader: target === 'SELFIE' ? 'Take Live Selfie' : 'Capture ID Document',
        source: target === 'SELFIE' ? 'camera' : 'prompt',
      });
      if (nativePhoto?.dataUrl) {
        if (target === 'FRONT') setFrontImage(nativePhoto.dataUrl);
        else if (target === 'BACK') setBackImage(nativePhoto.dataUrl);
        else if (target === 'SELFIE') setSelfieImage(nativePhoto.dataUrl);
        return;
      }
    }

    setCameraTarget(target);
    setCameraError(null);
    setCameraModalOpen(true);

    try {
      if (cameraStream) {
        cameraStream.getTracks().forEach((t) => t.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: target === 'SELFIE' ? 'user' : 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(console.warn);
      }
    } catch (err: any) {
      console.warn('Camera access denied or unavailable:', err);
      setCameraError('Camera access unavailable. You can use the "Upload Photo" button instead.');
    }
  };

  const capturePhotoFromVideo = () => {
    if (!videoRef.current) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 1280;
    canvas.height = videoRef.current.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // If selfie, mirror image for natural feeling
    if (cameraTarget === 'SELFIE') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);

    if (cameraTarget === 'FRONT') setFrontImage(dataUrl);
    else if (cameraTarget === 'BACK') setBackImage(dataUrl);
    else if (cameraTarget === 'SELFIE') setSelfieImage(dataUrl);

    stopCameraModal();
  };

  const stopCameraModal = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((t) => t.stop());
      setCameraStream(null);
    }
    setCameraModalOpen(false);
  };

  const dataUrlToFile = async (source: string, fallbackName: string): Promise<File> => {
    const res = await window.fetch(source);
    const blob = await res.blob();
    const mimeType = blob.type || 'image/jpeg';
    const ext = mimeType.includes('pdf') ? 'pdf' : mimeType.includes('png') ? 'png' : 'jpg';
    return new File([blob], `${fallbackName}.${ext}`, { type: mimeType });
  };

  // Submit KYC to Firebase Storage + Firestore and Backend
  const handleSubmitKYC = async () => {
    setValidationError(null);
    setDobError(null);

    const dobCheck = validateAdultDob(dob, new Date(), gender);
    if (!dobCheck.valid) {
      const errText = dobCheck.error || 'Not eligible — your age does not meet the minimum requirement.';
      setDobError(errText);
      setValidationError(errText);
      return;
    }

    // Centralized document & side completion enforcement
    const validationResult = validateProfileSubmission({
      documentType: selectedDocType,
      front: frontImage,
      back: backImage,
      selfie: selfieImage,
      requireSelfie: true,
      legalName: legalName,
    });

    if (!validationResult.canSubmit) {
      setValidationError(validationResult.errors[0] || validationResult.summaryMessage);
      return;
    }

    setIsSubmitting(true);
    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser) throw new Error('Please sign in before submitting KYC.');
      const idToken = await firebaseUser.getIdToken();
      const docTypeMap: Record<KYCDocumentType, SupportedDocumentType> = {
        AADHAAR: 'aadhaar',
        VOTER_ID: 'voter_id',
        PAN: 'pan',
        DRIVING_LICENSE: 'driving_license',
        PASSPORT: 'passport',
      };
      const supportedDocType = docTypeMap[selectedDocType];

      // 1. Upload Front document to Firebase Storage + Firestore metadata
      let frontStorageUrl = frontImage || '';
      if (frontImage && frontImage.startsWith('data:')) {
        const frontFile = await dataUrlToFile(frontImage, `${supportedDocType}_front`);
        const frontRec = await uploadDocumentSide(
          firebaseUser.uid,
          supportedDocType,
          'front',
          frontFile,
          { idNumber: idNumber.trim() || undefined, legalName: legalName.trim() }
        );
        frontStorageUrl = frontRec.front.downloadUrl || frontRec.front.storagePath || '';
      }

      // 2. Upload Back document (if required) to Firebase Storage + Firestore metadata
      let backStorageUrl = docConfig.sidesRequired === 'BOTH' ? backImage || undefined : undefined;
      if (docConfig.sidesRequired === 'BOTH' && backImage && backImage.startsWith('data:')) {
        const backFile = await dataUrlToFile(backImage, `${supportedDocType}_back`);
        const backRec = await uploadDocumentSide(
          firebaseUser.uid,
          supportedDocType,
          'back',
          backFile,
          { idNumber: idNumber.trim() || undefined, legalName: legalName.trim() }
        );
        backStorageUrl = backRec.back.downloadUrl || backRec.back.storagePath || undefined;
      }

      if (selfieImage && selfieImage.startsWith('data:')) {
        try {
          const selfieFile = await dataUrlToFile(selfieImage, 'live_selfie');
          const selfieRef = ref(storage, `verification/${firebaseUser.uid}/selfie/${selfieFile.name}`);
          await uploadBytes(selfieRef, selfieFile, { contentType: selfieFile.type });
        } catch {
          // Best-effort Firebase Storage selfie upload; Neon KYC API remains authoritative
        }
      }

      // Selfie verification is not yet supported by the Neon KYC API. Do not upload it to a separate legacy path.

      // Submit documents once through the authoritative Neon-backed API
      await submitDocumentsForReview(firebaseUser.uid, { name: userName, email: userEmail, phone: userPhone });
// Neon-backed API already accepted the document uploads.
      // Submit once through the authoritative backend; do not call the legacy memory endpoint.
setCurrentSubmission(null);
      setKycStatus('PENDING_REVIEW');
      setSubmitSuccessMessage(
        'Identity documents submitted for administrator review. Selfie verification is not yet confirmed by the backend.'
      );

      // Update local storage
      if (currentUserProfile) {
        const updated = {
          ...currentUserProfile,
          legalName: legalName.trim(),
          gender,
          dob: dobCheck.normalizedDob,
          isIdVerified: false,
          kycStatus: 'PENDING_REVIEW',
        };
        localStorage.setItem('pairup_user_profile_data', JSON.stringify(updated));
        setCurrentUserProfile(updated);
      }

      if (userRole === 'client') {
        setTimeout(() => {
          navigate('/#choose-a-service-section', { replace: true });
        }, 1200);
      }
    } catch (err: any) {
      setValidationError(err.message || 'Submission error. Please check your connection and retry.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const docConfig = DOCUMENT_CONFIGS[selectedDocType];
  const isBackRequired = docConfig.sidesRequired === 'BOTH';

  // Can submit check using centralized validation utility
  const isReadyToSubmit = canSubmitProfile({
    documentType: selectedDocType,
    front: frontImage,
    back: backImage,
    selfie: selfieImage,
    requireSelfie: true,
    legalName: legalName,
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pb-16 font-sans">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer"
              title="Go back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-pink-500 to-rose-600 flex items-center justify-center text-white shadow-md shadow-pink-950">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-base sm:text-lg font-black text-white tracking-tight flex items-center gap-2">
                  Government ID & KYC Verification
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-500/20 text-pink-300 font-mono font-bold border border-pink-500/30">
                    v1.0 Spec
                  </span>
                </h1>
                <p className="text-[11px] text-slate-400 hidden sm:block">
                  Aadhaar â€¢ PAN â€¢ Driving Licence â€¢ Passport â€¢ 100% Encrypted & Private
                </p>
              </div>
            </div>
          </div>

          {/* Quick Status Pill */}
          <div className="flex items-center gap-2">
            <span
              className={`px-3 py-1 rounded-full text-xs font-bold font-mono border flex items-center gap-1.5 ${
                kycStatus === 'APPROVED'
                  ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
                  : kycStatus === 'PENDING_REVIEW'
                  ? 'bg-amber-500/15 text-amber-300 border-amber-500/30'
                  : kycStatus === 'REUPLOAD_REQUIRED'
                  ? 'bg-purple-500/15 text-purple-300 border-purple-500/30 animate-pulse'
                  : kycStatus === 'REJECTED'
                  ? 'bg-rose-500/15 text-rose-400 border-rose-500/30'
                  : 'bg-slate-800 text-slate-400 border-slate-700'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-current" />
              {kycStatus.replace('_', ' ')}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
        {/* STATE MACHINE STATUS BANNER */}
        {kycStatus === 'APPROVED' && (
          <div className="mb-6 p-6 rounded-2xl bg-gradient-to-r from-emerald-950/60 to-slate-900 border border-emerald-500/40 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 border border-emerald-500/30">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-black text-emerald-300">KYC Verification Completed & Approved âœ“</h2>
                <p className="text-sm text-slate-300 mt-1">
                  Your identity has been verified by our compliance team against official records. You have full access to companion bookings, profile badges, and automated payouts.
                </p>
                <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-slate-400">
                  <span>Document: <strong className="text-white">{selectedDocType}</strong></span>
                  <span>â€¢</span>
                  <span>Verified Legal Name: <strong className="text-white">{legalName}</strong></span>
                  <span>â€¢</span>
                  <span>Review Date: <strong className="text-white">{currentSubmission?.reviewedAt ? new Date(currentSubmission.reviewedAt).toLocaleDateString() : 'Active'}</strong></span>
                </div>
              </div>
              <button
                onClick={() => navigate('/companion/dashboard')}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-xl transition cursor-pointer"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        )}

        {kycStatus === 'PENDING_REVIEW' && (
          <div className="mb-6 p-6 rounded-2xl bg-gradient-to-r from-amber-950/40 via-slate-900 to-slate-900 border border-amber-500/30 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-amber-500/20 text-amber-400 flex items-center justify-center shrink-0 border border-amber-500/30">
                <Clock className="w-7 h-7 animate-pulse" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-black text-amber-300">KYC Under Admin Review (à¦…à¦ªà§‡à¦•à§à¦·à¦®à¦¾à¦£)</h2>
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-mono">
                    ID: {currentSubmission?.id || 'KYC-PENDING'}
                  </span>
                </div>
                <p className="text-sm text-slate-300 mt-1">
                  Your government ID ({selectedDocType}) and live selfie were successfully received and queued for manual compliance vetting.
                  Average review time is <strong>2 to 4 hours</strong>.
                </p>
                <div className="mt-3 p-3 rounded-xl bg-slate-950/60 border border-slate-800 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
                  <span>🔒 Documents are stored in an encrypted vault and strictly inaccessible publicly.</span>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-400 font-mono">Submitted: {currentSubmission?.submittedAt ? new Date(currentSubmission.submittedAt).toLocaleTimeString() : 'Just now'}</span>
                    <button
                      type="button"
                      onClick={() => navigate(userRole === 'client' ? '/home' : '/companion/dashboard')}
                      className="px-3.5 py-1.5 rounded-xl bg-pink-600 hover:bg-pink-500 text-white font-bold text-xs transition cursor-pointer"
                    >
                      {userRole === 'client' ? 'Find Your Companion / Choose a Service' : 'Go to Companion Dashboard'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {kycStatus === 'REUPLOAD_REQUIRED' && (
          <div className="mb-6 p-6 rounded-2xl bg-gradient-to-r from-purple-950/50 via-slate-900 to-slate-900 border border-purple-500/50 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-purple-500/20 text-purple-400 flex items-center justify-center shrink-0 border border-purple-500/30">
                <RefreshCw className="w-7 h-7 animate-spin" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-black text-purple-300">Re-upload Required (à¦ªà§à¦¨à¦°à¦¾à¦¯à¦¼ à¦†à¦ªà¦²à§‹à¦¡ à¦ªà§à¦°à¦¯à¦¼à§‹à¦œà¦¨)</h2>
                <p className="text-sm text-slate-300 mt-1">
                  Our compliance officer reviewed your submission and requested a re-upload of your documents.
                </p>
                <div className="mt-3 p-3.5 rounded-xl bg-purple-950/40 border border-purple-500/30 text-xs text-purple-200">
                  <strong className="block text-purple-300 font-bold mb-1">Reason from Compliance Officer:</strong>
                  {currentSubmission?.reuploadNotes || 'Image corners cropped or blurred. Please upload clear, uncropped photos.'}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  Please update the affected document below and click <strong>"Submit Updated KYC"</strong>.
                </p>
              </div>
            </div>
          </div>
        )}

        {kycStatus === 'REJECTED' && (
          <div className="mb-6 p-6 rounded-2xl bg-gradient-to-r from-rose-950/50 via-slate-900 to-slate-900 border border-rose-500/40 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-rose-500/20 text-rose-400 flex items-center justify-center shrink-0 border border-rose-500/30">
                <AlertCircle className="w-7 h-7" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-black text-rose-400">KYC Verification Unsuccessful (à¦¬à¦¾à¦¤à¦¿à¦² à¦•à¦°à¦¾ à¦¹à¦¯à¦¼à§‡à¦›à§‡)</h2>
                <p className="text-sm text-slate-300 mt-1">
                  The submitted documents did not meet our verification criteria.
                </p>
                <div className="mt-3 p-3.5 rounded-xl bg-rose-950/40 border border-rose-500/30 text-xs text-rose-200">
                  <strong className="block text-rose-300 font-bold mb-1">Rejection Reason:</strong>
                  {currentSubmission?.rejectionReason || 'Name mismatch or expired document.'}
                </div>
                <p className="text-xs text-slate-400 mt-2">
                  You may re-apply with a valid, official government-issued ID below.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* STEP 1: SELECT GOVERNMENT ID TYPE */}
        <section className="bg-slate-900/90 rounded-2xl border border-slate-800 p-5 sm:p-6 mb-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="text-[11px] font-bold text-pink-400 tracking-wider uppercase">Step 1 of 3</span>
              <h2 className="text-lg font-black text-white">Select Government Identification Document</h2>
              <p className="text-xs text-slate-400">Supported Government IDs: Aadhaar, PAN, Driving Licence, Passport</p>
            </div>
            <Lock className="w-4 h-4 text-slate-500" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(Object.keys(DOCUMENT_CONFIGS) as KYCDocumentType[]).map((type) => {
              const cfg = DOCUMENT_CONFIGS[type];
              const isSelected = selectedDocType === type;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleSelectDocType(type)}
                  className={`p-4 rounded-xl text-left border transition-all cursor-pointer flex flex-col justify-between ${
                    isSelected
                      ? 'bg-gradient-to-b from-pink-950/40 to-slate-900 border-pink-500 shadow-md shadow-pink-950/30'
                      : 'bg-slate-950/60 border-slate-800 hover:border-slate-700 hover:bg-slate-900'
                  }`}
                >
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold font-mono text-pink-400">{type}</span>
                      {isSelected ? (
                        <CheckCircle2 className="w-4 h-4 text-pink-400" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border border-slate-700" />
                      )}
                    </div>
                    <div className="text-sm font-black text-white">{cfg.title}</div>
                    <div className="text-[11px] text-slate-400 mt-1 line-clamp-2">{cfg.subtitle}</div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-800/80 flex items-center justify-between text-[10px]">
                    <span className="text-slate-400">{cfg.badge}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Legal Name & Optional ID number */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5 pt-4 border-t border-slate-800">
            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1">
                Full Legal Name <span className="text-pink-400">*</span>
              </label>
              <input
                type="text"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                placeholder="Enter exact full name as printed on ID"
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-hidden focus:border-pink-500 transition"
              />
              <span className="text-[10px] text-slate-500 mt-1 block">
                Must match the name on your {docConfig.title}
              </span>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1">
                {docConfig.title} Number (Optional Masked)
              </label>
              <input
                type="text"
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value)}
                placeholder={docConfig.idFormatHint}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-hidden focus:border-pink-500 transition"
              />
              <span className="text-[10px] text-slate-500 mt-1 block">
                Helps expedite verification by compliance team
              </span>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1">
                Gender <span className="text-pink-400">*</span> (Male 21+ / Female 18+)
              </label>
              <select
                value={gender}
                onChange={(e) => {
                  setGender(e.target.value as 'Male' | 'Female');
                  setDobError(null);
                }}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-hidden focus:border-pink-500 transition"
              >
                <option value="Male">Male (Minimum 21 yrs)</option>
                <option value="Female">Female (Minimum 18 yrs)</option>
              </select>
            </div>

            <div>
              <DateOfBirthInput
                id="kyc-verification-dob"
                value={dob}
                gender={gender}
                label={`Date of Birth (${gender === 'Male' ? 'Must be 21+ yrs' : 'Must be 18+ yrs'})`}
                error={dobError}
                onChange={(nextDob) => {
                  setDob(nextDob);
                  setDobError(null);
                }}
                variant="dark"
              />
            </div>
          </div>
        </section>

        {/* STEP 2: DOCUMENT UPLOAD & CAPTURE SCREEN */}
        <section className="bg-slate-900/90 rounded-2xl border border-slate-800 p-5 sm:p-6 mb-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="text-[11px] font-bold text-pink-400 tracking-wider uppercase">Step 2 of 3</span>
              <h2 className="text-lg font-black text-white">Upload {docConfig.title} Images</h2>
              <p className="text-xs text-slate-400">
                {docConfig.sidesRequired === 'BOTH'
                  ? 'Both Front and Back sides are required (à¦¦à§à¦‡ à¦ªà¦¾à¦¶ à¦ªà§à¦°à¦¯à¦¼à§‹à¦œà¦¨)'
                  : 'Only the Front side is required for PAN Card (à¦¶à§à¦§à§ Front à¦ªà¦¾à¦¶)'}
              </p>
            </div>
            <span className="text-xs px-2.5 py-1 rounded-lg bg-slate-800 text-slate-300 font-mono">
              Max 10MB â€¢ JPG, PNG, PDF
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* FRONT SIDE CONTAINER */}
            <div className="bg-slate-950/80 rounded-2xl border border-slate-800 p-4 sm:p-5 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-black text-pink-400 uppercase tracking-wide">
                    {docConfig.frontLabel}
                  </span>
                  {frontImage ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-bold flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Ready
                    </span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-400 font-bold">
                      Required *
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400 mb-4">{docConfig.frontHelp}</p>

                {/* Preview Box */}
                {frontImage ? (
                  <div className="relative group rounded-xl overflow-hidden border border-slate-700 bg-slate-900 aspect-16/10 flex items-center justify-center">
                    {frontImage.startsWith('data:application/pdf') ? (
                      <div className="flex flex-col items-center justify-center p-4 text-center">
                        <FileText className="w-12 h-12 text-pink-400 mb-2" />
                        <span className="text-xs font-bold text-white">PDF Document Attached</span>
                        <span className="text-[10px] text-slate-400 mt-1">Ready for verification</span>
                      </div>
                    ) : (
                      <img
                        src={frontImage}
                        alt="Front Side Preview"
                        className="w-full h-full object-contain"
                      />
                    )}
                    {/* Watermark overlay */}
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-25">
                      <span className="text-xs font-mono font-black text-white rotate-[-15deg] uppercase tracking-widest border border-white px-2 py-1">
                        PAIRUP SECURE KYC
                      </span>
                    </div>
                    {/* Hover controls */}
                    <div className="absolute inset-0 bg-slate-950/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setPreviewModalUrl(frontImage);
                          setPreviewModalTitle(`${docConfig.title} â€” Front Side`);
                          setZoomLevel(1);
                          setRotationDegrees(0);
                        }}
                        className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center gap-1 cursor-pointer"
                      >
                        <Eye className="w-3.5 h-3.5" /> Inspect
                      </button>
                      <button
                        type="button"
                        onClick={() => setFrontImage(null)}
                        className="p-2 rounded-xl bg-rose-600/80 hover:bg-rose-600 text-white text-xs font-bold flex items-center gap-1 cursor-pointer"
                      >
                        <X className="w-3.5 h-3.5" /> Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border-2 border-dashed border-slate-800 hover:border-slate-700 bg-slate-900/40 p-6 flex flex-col items-center justify-center text-center aspect-16/10">
                    <FileText className="w-8 h-8 text-slate-600 mb-2" />
                    <span className="text-xs font-bold text-slate-300">Front side not uploaded</span>
                    <span className="text-[11px] text-slate-500 mt-1">Take a photo or choose document from gallery</span>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-slate-900">
                <button
                  type="button"
                  onClick={() => startCameraCapture('FRONT')}
                  className="px-3 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center justify-center gap-2 transition cursor-pointer"
                >
                  <Camera className="w-4 h-4 text-pink-400" />
                  <span>Take Photo</span>
                </button>

                <label className="px-3 py-2.5 rounded-xl bg-pink-600 hover:bg-pink-500 text-white text-xs font-bold flex items-center justify-center gap-2 transition cursor-pointer text-center">
                  <Upload className="w-4 h-4" />
                  <span>Choose from Gallery</span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,.webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) validateAndProcessFile(file, (url) => setFrontImage(url));
                    }}
                  />
                </label>
              </div>
            </div>

            {/* BACK SIDE CONTAINER (BOTH or NOT REQUIRED for PAN) */}
            {isBackRequired ? (
              <div className="bg-slate-950/80 rounded-2xl border border-slate-800 p-4 sm:p-5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-black text-pink-400 uppercase tracking-wide">
                      {docConfig.backLabel}
                    </span>
                    {backImage ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-bold flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Ready
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-400 font-bold">
                        Required *
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mb-4">{docConfig.backHelp}</p>

                  {/* Preview Box */}
                  {backImage ? (
                    <div className="relative group rounded-xl overflow-hidden border border-slate-700 bg-slate-900 aspect-16/10 flex items-center justify-center">
                      {backImage.startsWith('data:application/pdf') ? (
                        <div className="flex flex-col items-center justify-center p-4 text-center">
                          <FileText className="w-12 h-12 text-pink-400 mb-2" />
                          <span className="text-xs font-bold text-white">PDF Document Attached</span>
                          <span className="text-[10px] text-slate-400 mt-1">Ready for verification</span>
                        </div>
                      ) : (
                        <img
                          src={backImage}
                          alt="Back Side Preview"
                          className="w-full h-full object-contain"
                        />
                      )}
                      {/* Watermark overlay */}
                      <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-25">
                        <span className="text-xs font-mono font-black text-white rotate-[-15deg] uppercase tracking-widest border border-white px-2 py-1">
                          PAIRUP SECURE KYC
                        </span>
                      </div>
                      {/* Hover controls */}
                      <div className="absolute inset-0 bg-slate-950/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setPreviewModalUrl(backImage);
                            setPreviewModalTitle(`${docConfig.title} â€” Back Side`);
                            setZoomLevel(1);
                            setRotationDegrees(0);
                          }}
                          className="p-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center gap-1 cursor-pointer"
                        >
                          <Eye className="w-3.5 h-3.5" /> Inspect
                        </button>
                        <button
                          type="button"
                          onClick={() => setBackImage(null)}
                          className="p-2 rounded-xl bg-rose-600/80 hover:bg-rose-600 text-white text-xs font-bold flex items-center gap-1 cursor-pointer"
                        >
                          <X className="w-3.5 h-3.5" /> Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border-2 border-dashed border-slate-800 hover:border-slate-700 bg-slate-900/40 p-6 flex flex-col items-center justify-center text-center aspect-16/10">
                      <FileText className="w-8 h-8 text-slate-600 mb-2" />
                      <span className="text-xs font-bold text-slate-300">Back side not uploaded</span>
                      <span className="text-[11px] text-slate-500 mt-1">Take a photo or choose document from gallery</span>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-2 gap-2 mt-4 pt-3 border-t border-slate-900">
                  <button
                    type="button"
                    onClick={() => startCameraCapture('BACK')}
                    className="px-3 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center justify-center gap-2 transition cursor-pointer"
                  >
                    <Camera className="w-4 h-4 text-pink-400" />
                    <span>Take Photo</span>
                  </button>

                  <label className="px-3 py-2.5 rounded-xl bg-pink-600 hover:bg-pink-500 text-white text-xs font-bold flex items-center justify-center gap-2 transition cursor-pointer text-center">
                    <Upload className="w-4 h-4" />
                    <span>Choose from Gallery</span>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,.webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) validateAndProcessFile(file, (url) => setBackImage(url));
                      }}
                    />
                  </label>
                </div>
              </div>
            ) : (
              /* PAN Card Back Side â€” Not Required (Section 3 Spec) */
              <div className="bg-slate-950/40 rounded-2xl border border-dashed border-slate-800 p-4 sm:p-5 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-black text-slate-400 uppercase tracking-wide">
                      BACK SIDE
                    </span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-emerald-400 font-bold border border-emerald-500/30">
                      Not Required
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">
                    PAN Card is a single-sided official document. Back side upload is not required for PAN verification.
                  </p>

                  <div className="rounded-xl border border-slate-800/80 bg-slate-900/30 p-6 flex flex-col items-center justify-center text-center aspect-16/10">
                    <CheckCircle2 className="w-10 h-10 text-emerald-500/70 mb-2" />
                    <span className="text-xs font-bold text-slate-200">Back Side Not Required</span>
                    <span className="text-[11px] text-slate-400 mt-1">
                      PAN card validation only requires front side photo/document
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-900/80 text-center">
                  <span className="text-[11px] text-slate-500 font-medium">
                    âœ“ Ready to submit with front side only
                  </span>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* STEP 3: SELFIE / BIOMETRIC FACE VERIFICATION */}
        <section className="bg-slate-900/90 rounded-2xl border border-slate-800 p-5 sm:p-6 mb-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="text-[11px] font-bold text-pink-400 tracking-wider uppercase">Step 3 of 3</span>
              <h2 className="text-lg font-black text-white">Selfie & Face Liveness Verification</h2>
              <p className="text-xs text-slate-400">
                Match your face against the photo on your {docConfig.title} to verify legal identity ownership
              </p>
            </div>
            {selfieImage ? (
              <span className="text-[10px] px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-400 font-bold flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Face Photo Added
              </span>
            ) : (
              <span className="text-[10px] px-2.5 py-1 rounded-full bg-rose-500/20 text-rose-400 font-bold">
                Required *
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 items-center">
            {/* Selfie Preview or Placeholder */}
            <div className="sm:col-span-1 flex justify-center">
              {selfieImage ? (
                <div className="relative group w-40 h-48 rounded-2xl overflow-hidden border-2 border-emerald-500/50 shadow-lg bg-slate-950">
                  <img
                    src={selfieImage}
                    alt="Selfie preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-slate-950/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setPreviewModalUrl(selfieImage);
                        setPreviewModalTitle('Face Verification Selfie');
                        setZoomLevel(1);
                        setRotationDegrees(0);
                      }}
                      className="p-2 rounded-xl bg-slate-800 text-white text-xs font-bold"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelfieImage(null)}
                      className="p-2 rounded-xl bg-rose-600 text-white text-xs font-bold"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="w-40 h-48 rounded-2xl border-2 border-dashed border-slate-800 bg-slate-950/60 flex flex-col items-center justify-center text-center p-3">
                  <div className="w-12 h-12 rounded-full border border-slate-700 flex items-center justify-center text-slate-500 mb-2">
                    <UserCheck className="w-6 h-6" />
                  </div>
                  <span className="text-xs font-bold text-slate-400">Live Face Photo</span>
                  <span className="text-[10px] text-slate-600 mt-1">No sunglasses or masks</span>
                </div>
              )}
            </div>

            {/* Actions & Instructions */}
            <div className="sm:col-span-2 space-y-3">
              <div className="p-3.5 rounded-xl bg-slate-950/60 border border-slate-800 text-xs text-slate-300 space-y-1.5">
                <div className="flex items-center gap-2 text-pink-400 font-bold">
                  <Sparkles className="w-4 h-4" /> Verification Tips:
                </div>
                <p>â€¢ Look straight at the camera in a well-lit room.</p>
                <p>â€¢ Avoid flash reflections or heavy face filters.</p>
                <p>â€¢ Photo is securely encrypted and used strictly for internal KYC review.</p>
              </div>

              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => startCameraCapture('SELFIE')}
                  className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white text-xs font-bold flex items-center gap-2 transition cursor-pointer shadow-md shadow-pink-950"
                >
                  <Camera className="w-4 h-4" />
                  <span>Take Live Selfie</span>
                </button>

                <label className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold flex items-center gap-2 transition cursor-pointer">
                  <Upload className="w-4 h-4 text-slate-400" />
                  <span>Upload Face Photo</span>
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) validateAndProcessFile(file, (url) => setSelfieImage(url));
                    }}
                  />
                </label>
              </div>
            </div>
          </div>
        </section>

        {/* Validation Error Message */}
        {validationError && (
          <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-xs flex items-center gap-3">
            <AlertCircle className="w-5 h-5 shrink-0 text-rose-400" />
            <span>{validationError}</span>
          </div>
        )}

        {/* Submit Success Message */}
        {submitSuccessMessage && (
          <div className="mb-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-400" />
            <span>{submitSuccessMessage}</span>
          </div>
        )}

        {/* SUBMIT BUTTON BAR */}
        <div className="sticky bottom-4 z-20 bg-slate-900/95 backdrop-blur-md p-4 sm:p-5 rounded-2xl border border-slate-800 shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-xs text-slate-400">
            <div className="font-bold text-white flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-pink-400" />
              <span>Identity Protection Guarantee</span>
            </div>
            <span>By submitting, you certify that all uploaded documents are authentic and legally issued.</span>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs transition cursor-pointer"
            >
              Cancel
            </button>

            <button
              type="button"
              disabled={!isReadyToSubmit || isSubmitting}
              onClick={handleSubmitKYC}
              className={`flex-1 sm:flex-none px-6 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer shadow-lg ${
                isReadyToSubmit && !isSubmitting
                  ? 'bg-gradient-to-r from-pink-600 via-rose-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white shadow-pink-950'
                  : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
              }`}
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Submitting Encrypted Documents...</span>
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" />
                  <span>
                    {kycStatus === 'REUPLOAD_REQUIRED' ? 'Submit Updated KYC' : 'Submit KYC for Verification'}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </main>

      {/* SECURE DOCUMENT PREVIEW & INSPECTION MODAL */}
      {previewModalUrl && (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="p-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-pink-400" />
                <h3 className="text-sm font-bold text-white">{previewModalTitle}</h3>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setZoomLevel((z) => Math.min(z + 0.25, 3))}
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs cursor-pointer"
                  title="Zoom In"
                >
                  <ZoomIn className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setZoomLevel((z) => Math.max(z - 0.25, 0.5))}
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs cursor-pointer"
                  title="Zoom Out"
                >
                  <ZoomOut className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setRotationDegrees((r) => (r + 90) % 360)}
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs cursor-pointer"
                  title="Rotate 90Â°"
                >
                  <RotateCw className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setZoomLevel(1);
                    setRotationDegrees(0);
                  }}
                  className="px-2 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs cursor-pointer"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewModalUrl(null)}
                  className="p-1.5 rounded-lg bg-slate-800 hover:bg-rose-600 text-white text-xs cursor-pointer ml-2"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Modal Body / Viewer */}
            <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-slate-950 min-h-[300px] relative select-none">
              {previewModalUrl.startsWith('data:application/pdf') ? (
                <iframe
                  src={previewModalUrl}
                  title="PDF Document Preview"
                  className="w-full h-[65vh] rounded-lg border border-slate-800 bg-white"
                />
              ) : (
                <div
                  style={{
                    transform: `scale(${zoomLevel}) rotate(${rotationDegrees}deg)`,
                    transition: 'transform 0.2s ease-out',
                  }}
                  className="max-w-full max-h-full flex items-center justify-center relative"
                >
                  <img
                    src={previewModalUrl}
                    alt="High-res preview"
                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
                  />
                  {/* Watermark */}
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-30">
                    <span className="text-xl font-mono font-black text-pink-400 rotate-[-20deg] uppercase tracking-widest border-2 border-pink-400 px-4 py-2">
                      PAIRUP CONFIDENTIAL KYC
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-3 border-t border-slate-800 bg-slate-950/70 text-center text-[11px] text-slate-500 font-mono">
              Protected by Section 11/28 KYC Encryption â€¢ Authorized View Only
            </div>
          </div>
        </div>
      )}

      {/* LIVE CAMERA CAPTURE MODAL */}
      {cameraModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 animate-in fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl">
            <div className="p-4 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-pink-400" />
                <h3 className="text-sm font-bold text-white">
                  Capture {cameraTarget === 'SELFIE' ? 'Live Face Selfie' : `${docConfig.title} ${cameraTarget} Side`}
                </h3>
              </div>
              <button
                type="button"
                onClick={stopCameraModal}
                className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 bg-slate-950 relative flex items-center justify-center min-h-[300px]">
              {cameraError ? (
                <div className="p-4 text-center text-rose-300 text-xs">
                  <AlertCircle className="w-8 h-8 text-rose-400 mx-auto mb-2" />
                  <p>{cameraError}</p>
                </div>
              ) : (
                <div className="relative w-full aspect-4/3 rounded-xl overflow-hidden bg-black flex items-center justify-center">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                  {/* Framing Overlay Guide */}
                  {cameraTarget === 'SELFIE' ? (
                    <div className="absolute inset-0 border-2 border-dashed border-pink-400/60 rounded-full w-44 h-56 m-auto pointer-events-none flex items-center justify-center">
                      <span className="text-[10px] text-pink-300 bg-slate-950/60 px-2 py-0.5 rounded-full font-mono">
                        Align Face Here
                      </span>
                    </div>
                  ) : (
                    <div className="absolute inset-4 border-2 border-dashed border-pink-400/60 rounded-xl pointer-events-none flex items-center justify-center">
                      <span className="text-[10px] text-pink-300 bg-slate-950/60 px-2 py-0.5 rounded-full font-mono">
                        Fit ID Card Within Border
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-slate-800 flex items-center justify-between">
              <button
                type="button"
                onClick={stopCameraModal}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={Boolean(cameraError)}
                onClick={capturePhotoFromVideo}
                className={`px-5 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition cursor-pointer ${
                  cameraError
                    ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white shadow-md shadow-pink-950'
                }`}
              >
                <Camera className="w-4 h-4" />
                <span>Capture Photo</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KYCVerificationPage;




